import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function LocationManager() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/location").then((r) => setData(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/location", data);
      toast.success("Location updated");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const addHL = () => {
    setData({
      ...data,
      highlights: [...(data.highlights || []), { id: crypto.randomUUID(), title: "New highlight", description: "" }],
    });
  };

  const updHL = (i, field, val) => {
    const hl = [...(data.highlights || [])];
    hl[i] = { ...hl[i], [field]: val };
    setData({ ...data, highlights: hl });
  };

  const delHL = (i) => {
    const hl = [...(data.highlights || [])];
    hl.splice(i, 1);
    setData({ ...data, highlights: hl });
  };

  if (!data) return <div className="text-earth-600">Loading…</div>;

  return (
    <div data-testid="location-manager">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Edit</div>
        <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Location</h1>
      </div>

      <div className="bg-white rounded-2xl p-8 border border-earth-100 flex flex-col gap-5">
        <div>
          <Label>Address</Label>
          <Textarea className="mt-2" rows={2} value={data.address || ""} onChange={(e) => setData({ ...data, address: e.target.value })} data-testid="location-address" />
        </div>
        <div>
          <Label>Google Maps Embed URL</Label>
          <Input className="mt-2" value={data.map_embed_url || ""} onChange={(e) => setData({ ...data, map_embed_url: e.target.value })} data-testid="location-map" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <Label>Highlights</Label>
            <Button size="sm" onClick={addHL} className="bg-sage-500 text-white hover:bg-sage-600 rounded-full" data-testid="add-highlight-btn">
              <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Add
            </Button>
          </div>
          <div className="flex flex-col gap-3">
            {(data.highlights || []).map((h, i) => (
              <div key={h.id} className="flex gap-3 items-start bg-earth-50 rounded-xl p-4 border border-earth-100">
                <div className="flex-1 flex flex-col gap-2">
                  <Input placeholder="Title" value={h.title} onChange={(e) => updHL(i, "title", e.target.value)} data-testid={`highlight-title-${i}`} />
                  <Input placeholder="Description" value={h.description} onChange={(e) => updHL(i, "description", e.target.value)} data-testid={`highlight-desc-${i}`} />
                </div>
                <Button variant="ghost" size="icon" onClick={() => delHL(i)} data-testid={`highlight-del-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Button onClick={save} disabled={saving} className="bg-earth-900 text-white hover:bg-earth-800 rounded-full px-8" data-testid="location-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
